import { useMemo, useState } from "react";
import { CheckboxField } from "#/components/common/CheckboxField";
import { TextArea } from "#/components/common/TextArea";
import { TextInput } from "#/components/common/TextInput";
import type { DepartmentCreateRequest, DepartmentResponse } from "../types";

type Props = {
  initialValue?: DepartmentResponse | null;
  busy?: boolean;
  onSubmit: (payload: DepartmentCreateRequest) => Promise<void> | void;
};

type Errors = Partial<Record<keyof DepartmentCreateRequest, string>>;

export function DepartmentForm({ initialValue, busy = false, onSubmit }: Props) {
  const initialState = useMemo<DepartmentCreateRequest>(
    () => ({
      code: initialValue?.code ?? "",
      name: initialValue?.name ?? "",
      description: initialValue?.description ?? "",
      active: initialValue?.active ?? true,
    }),
    [initialValue]
  );

  const [form, setForm] = useState<DepartmentCreateRequest>(initialState);
  const [errors, setErrors] = useState<Errors>({});

  const validate = () => {
    const nextErrors: Errors = {};

    if (!form.code.trim()) nextErrors.code = "Code is required";
    if (!form.name.trim()) nextErrors.name = "Name is required";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    await onSubmit({
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description?.trim() || "",
      active: form.active,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        <TextInput
          label="Department code"
          value={form.code}
          error={errors.code}
          onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
          placeholder="e.g. HR"
        />

        <TextInput
          label="Department name"
          value={form.name}
          error={errors.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="e.g. Human Resource"
        />
      </div>

      <TextArea
        label="Description"
        value={form.description ?? ""}
        onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
        placeholder="Department summary..."
      />

      <CheckboxField
        label="Department is active"
        checked={form.active}
        onChange={(checked) => setForm((prev) => ({ ...prev, active: checked }))}
      />

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          className="rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          {busy ? "Saving..." : initialValue ? "Update department" : "Create department"}
        </button>
      </div>
    </form>
  );
}